import { useEffect, useState } from "react";
import { Download } from "lucide-react";
import { api } from "@/lib/api";
import { AdminPage, Pill } from "@/pages/admin/AdminLayout";

const RANGES = [
  { key: "today", label: "Today" },
  { key: "7d", label: "Last 7 days" },
  { key: "30d", label: "Last 30 days" },
  { key: "90d", label: "Last 90 days" },
  { key: "custom", label: "Custom" },
];

const EXPORTS = [
  { key: "acquisition", label: "Acquisition" },
  { key: "sources", label: "Sources" },
  { key: "landing_pages", label: "Landing pages" },
  { key: "content", label: "Content" },
  { key: "affiliates", label: "Affiliate performance" },
  { key: "journey", label: "Affiliate journey" },
];

const Metric = ({ label, value, note, testId }) => (
  <div className="bg-white border border-line px-5 py-5" data-testid={testId}>
    <div className="text-[9.5px] tracking-[0.2em] uppercase text-[#736F68]">{label}</div>
    <div className="display text-[30px] leading-none text-charcoal mt-3">
      {value === null || value === undefined ? "—" : value}
    </div>
    {note && <div className="text-[11.5px] font-light text-[#6E6A63] mt-2">{note}</div>}
  </div>
);

const Table = ({ title, columns, rows, empty = "No data in this range yet.", testId }) => (
  <div className="bg-white border border-line mt-6" data-testid={testId}>
    <div className="px-5 py-4 border-b border-line text-[11px] tracking-[0.18em] uppercase text-[#4F4B45]">
      {title}
    </div>
    {rows.length === 0 ? (
      <p className="px-5 py-6 text-[13.5px] font-light text-[#6E6A63]">{empty}</p>
    ) : (
      <div className="overflow-x-auto">
        <table className="w-full text-[13px]">
          <thead>
            <tr className="text-left text-[10px] tracking-[0.14em] uppercase text-[#7A766F]">
              {columns.map((c) => (
                <th key={c} className="px-5 py-3 font-medium border-b border-line whitespace-nowrap">
                  {c}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i} className="border-b border-line/70 last:border-0">
                {r.map((cell, j) => (
                  <td key={j} className="px-5 py-3 text-[#3F3B36] align-top whitespace-nowrap">
                    {cell === null || cell === undefined || cell === "" ? "—" : cell}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )}
  </div>
);

export default function Analytics() {
  const [range, setRange] = useState("30d");
  const [custom, setCustom] = useState({ start: "", end: "" });
  const [d, setD] = useState(null);
  const [error, setError] = useState("");

  const params = () =>
    range === "custom"
      ? { range, start: custom.start, end: custom.end }
      : { range };

  useEffect(() => {
    if (range === "custom" && !custom.start) return;
    setD(null);
    setError("");
    api
      .get("/admin/analytics", { params: params() })
      .then((r) => setD(r.data))
      .catch(() => setError("Could not load analytics."));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [range, custom.start, custom.end]);

  const download = async (report) => {
    const r = await api.get("/admin/analytics/export", {
      params: { ...params(), report },
      responseType: "blob",
    });
    const url = window.URL.createObjectURL(new Blob([r.data], { type: "text/csv" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = `hhl-${report}-${range}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  };

  const t = d?.traffic;
  const acq = d?.acquisition;

  return (
    <AdminPage
      title="Analytics"
      lede="First-party, anonymous reporting: traffic source, landing page, content, partner and outbound affiliate click. No names, emails, phone numbers, IP addresses or precise location are collected."
    >
      <div className="flex flex-wrap items-center gap-2" data-testid="analytics-ranges">
        {RANGES.map((r) => (
          <button
            key={r.key}
            onClick={() => setRange(r.key)}
            data-testid={`analytics-range-${r.key}`}
            className={`px-4 py-2 text-[11.5px] tracking-[0.12em] uppercase border transition-colors ${
              range === r.key
                ? "bg-charcoal text-white border-charcoal"
                : "bg-white text-[#4F4B45] border-line hover:border-charcoal/40"
            }`}
          >
            {r.label}
          </button>
        ))}
        {range === "custom" && (
          <span className="flex items-center gap-2 ml-1">
            <input
              type="date"
              value={custom.start}
              data-testid="analytics-custom-start"
              onChange={(e) => setCustom((c) => ({ ...c, start: e.target.value }))}
              className="border border-line px-3 py-2 text-[12.5px] bg-white"
            />
            <span className="text-[12px] text-[#7A766F]">to</span>
            <input
              type="date"
              value={custom.end}
              data-testid="analytics-custom-end"
              onChange={(e) => setCustom((c) => ({ ...c, end: e.target.value }))}
              className="border border-line px-3 py-2 text-[12.5px] bg-white"
            />
          </span>
        )}
      </div>

      {error && (
        <p className="mt-6 text-[13.5px] text-[#991B1B]" data-testid="analytics-error">
          {error}
        </p>
      )}

      {!d && !error && (
        <p className="mt-8 text-[13.5px] font-light text-[#6E6A63]" data-testid="analytics-loading">
          Loading {range === "custom" ? "custom range" : "report"}…
        </p>
      )}

      {d && (
        <div data-testid="analytics-report">
          <div className="mt-6 flex flex-wrap items-center gap-3 text-[12.5px] font-light text-[#6A665F]">
            <Pill tone="sage">{d.range.label}</Pill>
            <span data-testid="analytics-enhanced-since">
              Enhanced tracking active since{" "}
              {d.enhanced_since ? new Date(d.enhanced_since).toLocaleString() : "not yet recorded"}
            </span>
            <span>·</span>
            <span data-testid="analytics-history-note">
              {d.history.total_events_all_time.toLocaleString()} events stored all time
            </span>
          </div>
          <p className="mt-3 text-[12.5px] font-light text-[#6E6A63] max-w-4xl leading-relaxed">
            {d.history.note} A visitor here means an anonymous browser or device profile, not a
            verified person: separate browsers, separate devices or cleared local storage each count
            separately.
          </p>

          <div className="flex flex-wrap gap-2 mt-6" data-testid="analytics-exports">
            {EXPORTS.map((e) => (
              <button
                key={e.key}
                onClick={() => download(e.key)}
                data-testid={`analytics-export-${e.key}`}
                className="inline-flex items-center gap-2 px-3.5 py-2 bg-white border border-line text-[11.5px] tracking-[0.1em] uppercase text-[#4F4B45] hover:border-charcoal/40 transition-colors"
              >
                <Download size={13} /> {e.label}
              </button>
            ))}
          </div>

          <h2 className="text-base md:text-lg font-medium text-charcoal mt-10">Traffic</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-line mt-4">
            <Metric label="Anonymous visitors" value={t.visitors} note="Browser / device profiles" testId="metric-visitors" />
            <Metric label="New" value={t.new_visitors} note="First session seen" testId="metric-new-visitors" />
            <Metric label="Returning" value={t.returning_visitors} note="Seen before this range" testId="metric-returning-visitors" />
            <Metric label="Sessions" value={t.sessions} note={`${t.new_sessions} new · ${t.returning_sessions} returning`} testId="metric-sessions" />
            <Metric label="Sessions per visitor" value={t.sessions_per_visitor} testId="metric-sessions-per-visitor" />
            <Metric label="Page views" value={t.page_views} note={`${t.legacy_page_views} pre-upgrade`} testId="metric-page-views" />
            <Metric label="Pages per session" value={t.pages_per_session} testId="metric-pages-per-session" />
            <Metric label="Affiliate clicks" value={d.totals.clicks} note={`${d.totals.impressions} impressions`} testId="metric-clicks" />
          </div>

          <h2 className="text-base md:text-lg font-medium text-charcoal mt-12">Acquisition</h2>
          <Table
            testId="table-acquisition"
            title="Channel"
            columns={["Channel", "Sessions", "Affiliate clicks"]}
            rows={acq.groups.map((r) => [r.group, r.sessions, r.clicks])}
          />
          <Table
            testId="table-sources"
            title="Source and medium"
            columns={["Source", "Medium", "Channel", "Sessions", "Clicks"]}
            rows={acq.sources.map((r) => [r.source, r.medium, r.group, r.sessions, r.clicks])}
          />
          <Table
            testId="table-campaigns"
            title="UTM campaigns"
            columns={["Campaign", "Source", "Sessions", "Clicks"]}
            rows={acq.campaigns.map((r) => [r.campaign, r.source, r.sessions, r.clicks])}
            empty="No campaign-tagged sessions in this range."
          />
          <Table
            testId="table-referrers"
            title="Referring domains"
            columns={["Domain", "Sessions"]}
            rows={acq.referrers.map((r) => [r.host, r.sessions])}
          />
          <Table
            testId="table-landing-pages"
            title="Landing pages"
            columns={["Landing page", "Type", "Sessions", "Affiliate clicks"]}
            rows={acq.landing_pages.map((r) => [r.page, r.type, r.sessions, r.clicks])}
          />
          <Table
            testId="table-devices"
            title="Devices"
            columns={["Device", "Sessions", "Affiliate clicks"]}
            rows={acq.devices.map((r) => [r.device, r.sessions, r.clicks])}
          />

          <h2 className="text-base md:text-lg font-medium text-charcoal mt-12">Content</h2>
          <Table
            testId="table-content-types"
            title="Views by page type"
            columns={["Type", "Views"]}
            rows={d.content.by_type.map((r) => [r.type, r.views])}
          />
          <Table
            testId="table-top-pages"
            title="Top pages"
            columns={["Page", "Views"]}
            rows={d.content.top_pages.map((r) => [r.page, r.views])}
          />
          <Table
            testId="table-top-guides"
            title="Guides"
            columns={["Guide", "Views"]}
            rows={d.content.top_guides.map((r) => [r.slug, r.views])}
          />
          <Table
            testId="table-top-hubs"
            title="Categories and hubs"
            columns={["Hub", "Views"]}
            rows={d.content.top_hubs.map((r) => [r.slug, r.views])}
          />
          <Table
            testId="table-top-tools"
            title="Tools"
            columns={["Tool", "Views"]}
            rows={d.content.top_tools.map((r) => [r.slug, r.views])}
          />

          <h2 className="text-base md:text-lg font-medium text-charcoal mt-12">
            Affiliate performance
          </h2>
          <p className="text-[12.5px] font-light text-[#6A665F] mt-2 max-w-4xl leading-relaxed">
            CTR is clicks divided by recorded views (partner impressions plus product views).
            Before the enhanced tracking start date, offer-grid cards recorded no view event at
            all, so any partner whose clicks came mainly from guide grids can show a CTR above 100%
            for ranges that include pre-upgrade days. Post-upgrade ranges are the reliable ones.
          </p>
          <Table
            testId="table-affiliates"
            title="By partner"
            columns={["Partner", "Impressions", "Product views", "Clicks", "CTR (clicks / views)",
                      "Sessions", "Visitors", "New", "Returning", "Top page", "Top source",
                      "Top device"]}
            rows={d.affiliates.map((r) => [
              r.merchant,
              r.impressions,
              r.product_views,
              r.clicks,
              r.ctr === null ? "—" : `${r.ctr}%`,
              r.click_sessions,
              r.click_visitors,
              r.new_visitor_clicks,
              r.returning_visitor_clicks,
              r.top_pages[0]?.key,
              r.sources[0]?.key,
              r.devices[0]?.key,
            ])}
          />
          <Table
            testId="table-affiliate-landing"
            title="Partner clicks by landing page and social source"
            columns={["Partner", "Top landing page", "Social source", "Top campaign", "Top product"]}
            rows={d.affiliates.map((r) => [
              r.merchant,
              r.top_landing_pages[0] ? `${r.top_landing_pages[0].key} (${r.top_landing_pages[0].count})` : "",
              r.social.map((s) => `${s.key} ${s.count}`).join(", "),
              r.campaigns[0]?.key,
              r.products[0]?.key,
            ])}
          />

          <h2 className="text-base md:text-lg font-medium text-charcoal mt-12">
            Social profile clicks
          </h2>
          <Table
            testId="table-social"
            title="Owned social links"
            columns={["Platform", "Page", "Placement", "Traffic source", "Device", "Clicks"]}
            rows={(d.social || []).map((r) => [
              r.platform, r.page, r.placement, r.source, r.device, r.clicks,
            ])}
            empty="No social profile clicks in this range yet."
          />

          <h2 className="text-base md:text-lg font-medium text-charcoal mt-12">
            Affiliate journey
          </h2>
          <p className="text-[13px] font-light text-[#6A665F] mt-2 max-w-3xl">
            Traffic source → landing page → category, guide or product → partner → outbound click.
            Rows without a landing page are pre-upgrade clicks, which carry no attribution.
          </p>
          <Table
            testId="table-journey"
            title="Source to click"
            columns={["Traffic source", "Landing page", "Content", "Product", "Partner", "Clicks",
                      "Sessions"]}
            rows={d.journey.map((r) => [
              r.source, r.landing_page, r.content, r.product, r.merchant, r.clicks, r.sessions,
            ])}
          />
        </div>
      )}
    </AdminPage>
  );
}
